import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";

export const PHONE_DISPLAY = "+61 0401 712 775";
export const PHONE_TEL = "tel:+61401712775";

const NAV = [
  { label: "Capabilities", href: "#capabilities", testId: "nav-capabilities-link" },
  { label: "Why Coastal", href: "#why-coastal", testId: "nav-why-coastal-link" },
  { label: "About James", href: "#about", testId: "nav-about-link" },
  { label: "Contact", href: "#contact", testId: "nav-contact-link" },
];

export const Wordmark = ({ imgClass = "h-9 md:h-10" }) => (
  <a href="#top" data-testid="logo-wordmark" className="inline-flex group" aria-label="Coastal Logistics Solutions — home">
    <span className="rounded-md bg-[#FCFBF4] px-3 py-1.5 shadow-[0_2px_12px_rgba(0,0,0,0.25)] group-hover:shadow-[0_2px_18px_rgba(45,212,191,0.25)] transition-shadow duration-300">
      <img src="/logo.png" alt="Coastal Logistics Solutions" className={`${imgClass} w-auto`} />
    </span>
  </a>
);

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "bg-[#02060D]/80 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-10 h-[72px] flex items-center justify-between">
        <Wordmark />
        <nav className="hidden md:flex items-center gap-8" aria-label="Primary">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={item.testId}
              className="text-sm text-slate-300 hover:text-teal-300 transition-colors duration-300 tracking-wide"
            >
              {item.label}
            </a>
          ))}
          <a
            href={PHONE_TEL}
            data-testid="header-call-james-btn"
            className="inline-flex items-center gap-2 rounded-full bg-teal-400 px-5 py-2.5 text-sm font-semibold text-[#02060D] hover:bg-teal-300 hover:shadow-[0_0_24px_rgba(45,212,191,0.35)] transition-[background-color,box-shadow] duration-300"
          >
            <Phone className="h-4 w-4" />
            Call James
          </a>
        </nav>
        <button
          type="button"
          data-testid="mobile-menu-btn"
          aria-label={open ? "Close menu" : "Open menu"}
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-slate-200 p-2"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#02060D]/95 backdrop-blur-xl border-b border-white/10"
            aria-label="Mobile"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  data-testid={`mobile-${item.testId}`}
                  onClick={() => setOpen(false)}
                  className="text-lg text-slate-200 hover:text-teal-300 transition-colors duration-300"
                >
                  {item.label}
                </a>
              ))}
              <a
                href={PHONE_TEL}
                data-testid="mobile-call-james-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-teal-400 px-5 py-3 text-sm font-semibold text-[#02060D]"
              >
                <Phone className="h-4 w-4" />
                Call James — {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
